"use client";
import { z } from "zod";
import Link from "next/link";
import { useState } from "react";
import axios from "@config/axios";
import { AxiosError } from "axios";
import { encrypt } from "@lib/encrypt";
import { useForm } from "react-hook-form";
import { Input } from "@components/ui/input";
import { LoginSchema } from "@schemas/login";
import { toast } from "@components/ui/toaster";
import { Button } from "@components/ui/button";
import { useMutation } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { LockKeyhole, LogIn, Mail } from "lucide-react";
import usePasswordToggle from "@hooks/usePasswordToggle";
import { useRouter, useSearchParams } from "next/navigation";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@components/ui/form";

const LoginForm = () => {
	const router = useRouter();
	const searchParams = useSearchParams();
	const urlParams = new URLSearchParams(searchParams?.toString() || "");
	const { inputIcon, inputType } = usePasswordToggle();
	const [onLoginToast, setOnLoginToast] = useState<string | number>();

	const defaultValues: z.infer<typeof LoginSchema> = {
		email: "",
		password: "",
	};

	const form = useForm<z.infer<typeof LoginSchema>>({
		defaultValues: defaultValues,
		mode: "onTouched",
		resolver: zodResolver(LoginSchema),
	});

	const onLogin = async (values: z.infer<typeof LoginSchema>) => {
		setOnLoginToast(toast.loading("Loading...", { description: "Logging in..." }));
		const { email } = values;
		const password = encrypt(values?.password);
		await axios.post("/api/auth/login", { email, password });
		return values;
	};

	const { mutate, isPending } = useMutation({
		mutationFn: onLogin,
		onSuccess: (values: z.infer<typeof LoginSchema>) => {
			toast.dismiss(onLoginToast);
			urlParams.set("email", values?.email);
			urlParams.set("type", "VERIFICATION");
			router.replace(`/auth/verify${urlParams ? `?${urlParams.toString()}` : ""}`);
		},
		onError: (error: unknown) => {
			toast.error("Error!", { id: onLoginToast, description: (error as AxiosError<string>)?.response?.data || "Internal server error!" });
		},
	});
	return (
		<Form {...form}>
			<form className="flex w-full max-w-sm flex-col items-center justify-stretch gap-2" onSubmit={form.handleSubmit(values => mutate(values))}>
				<div className="flex w-full flex-col items-center justify-center gap-2">
					<FormField
						control={form.control}
						name="email"
						render={({ field }) => (
							<FormItem className="w-full">
								<FormLabel>
									Email<span className="text-destructive">*</span>
								</FormLabel>
								<FormControl>
									<Input placeholder="Enter your email" startContent={<Mail size={16} />} {...field} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
					<FormField
						control={form.control}
						name="password"
						render={({ field }) => (
							<FormItem className="w-full">
								<FormLabel>
									Password<span className="text-destructive">*</span>
								</FormLabel>
								<FormControl>
									<Input endContent={inputIcon} placeholder="Enter your password" startContent={<LockKeyhole size={16} />} type={inputType} {...field} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
					<Link className="text-primary w-full text-end text-xs font-medium md:text-sm" href={`/auth/forgot-password${urlParams ? `?${urlParams.toString()}` : ""}`}>
						Forgot Password?
					</Link>
				</div>
				<div className="flex w-full flex-col items-center justify-center gap-6">
					<Button className="w-36 justify-between" disabled={isPending} size="lg" type="submit" variant="default">
						Login
						<LogIn size={16} />
					</Button>
					<span className="text-xs md:text-sm">
						{`Don't have an account? `}
						<Link className="text-secondary text-xs font-medium md:text-sm" href={`/auth/register${urlParams ? `?${urlParams.toString()}` : ""}`}>
							{`Register now`}
						</Link>
					</span>
				</div>
			</form>
		</Form>
	);
};

export default LoginForm;
